import {useEffect} from 'react';
import {HelmetProvider} from 'react-helmet-async';
import {Navigate, Route, BrowserRouter as Router, Routes, useLocation} from 'react-router-dom';
import './App.css';
import {ClickTrackingProvider} from './components/analytics/ClickTrackingProvider';

// Landing page
import Landing from './pages/landing/Landing';

// Stripe session management
import {StripeSessionProvider} from './contexts/StripeSessionContext';
import {useStripeSessionHandler} from './hooks/useStripeSessionHandler';

import {useAuthStore} from './store/authStore';
import {Events, trackEvent} from './utils/firebase';

function App() {
  const {user, isLoading, initialize} = useAuthStore();
  const stripeSessionState = useStripeSessionHandler();

  useEffect(() => {
    const init = async () => {
      try {
        await initialize();

        // Check for checkout success in URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (sessionId) {
          // Clean URL without reloading
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      }
    };

    init();

    // Helper function to map path to title when document.title is not set
    const mapPathToTitle = (path: string): string => {
      const routes: Record<string, string> = {
        '/': 'Home',
        '/pricing': 'Pricing',
        '/login': 'Login',
        '/login/confirm': 'Login Confirmation',
        '/register': 'Register',
        '/editor': 'Vision Editor',
        '/dashboard': 'Dashboard',
        '/account': 'Account',
        '/plan': 'Subscription Plan',
        '/preferences': 'Preferences',
        '/checkout': 'Checkout',
        '/compare': 'Comparisons',
      };

      return routes[path] || 'OpticWave';
    };

    // Track page view when component mounts
    const currentPath = window.location.pathname;
    trackEvent(Events.PAGE_VIEW, {
      path: currentPath,
      title: mapPathToTitle(currentPath),
      userId: user?.uid || 'anonymous',
      timestamp: new Date().toISOString(),
    });

    // Set up route change listener for SPA navigation
    const handleRouteChange = () => {
      const path = window.location.pathname;

      // Give the document title a moment to update after route change
      setTimeout(() => {
        trackEvent(Events.PAGE_VIEW, {
          path,
          title: mapPathToTitle(currentPath),
          userId: user?.uid || 'anonymous',
          timestamp: new Date().toISOString(),
        });
      }, 100);
    };

    // Create a MutationObserver to detect URL changes
    const observer = new MutationObserver(() => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPathRef.current) {
        lastPathRef.current = currentPath;
        handleRouteChange();
      }
    });

    // Track URL changes by observing the document
    const lastPathRef = {current: window.location.pathname};
    observer.observe(document, {subtree: true, childList: true});

    // Clean up observer on component unmount
    return () => observer.disconnect();
  }, [user, initialize]);

  // Return loading state if not initialized
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <StripeSessionProvider value={stripeSessionState}>
      <Routes>
        {/* Public routes - only landing page accessible */}
        <Route path="/" element={<Landing />} />

        {/* All other routes redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </StripeSessionProvider>
  );
}

// ScrollToTop component to reset scroll position on page navigation
const ScrollToTop = () => {
  const {pathname} = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Create a wrapper component that includes only one Router
function AppWrapper() {
  return (
    <HelmetProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <ClickTrackingProvider>
          <App />
        </ClickTrackingProvider>
      </Router>
    </HelmetProvider>
  );
}

export default AppWrapper;
